import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Schedule from "./pages/Schedule";
import Patrol from "./pages/Patrol";
import Incidents from "./pages/Incidents";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSites from "./pages/admin/AdminSites";
import AdminSchedules from "./pages/admin/AdminSchedules";
import AdminTracking from "./pages/admin/AdminTracking";
import AdminIncidents from "./pages/admin/AdminIncidents";
import AdminReports from "./pages/admin/AdminReports";
import AdminOrganization from "./pages/admin/AdminOrganization";
import AdminJoinRequests from "./pages/admin/AdminJoinRequests";
import AdminTimesheets from "./pages/admin/AdminTimesheets";
import AdminApprovals from "./pages/admin/AdminApprovals";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminActivityTimeline from "./pages/admin/AdminActivityTimeline";
import PlatformDashboard from "./pages/platform/PlatformDashboard";
import PlatformOrganizations from "./pages/platform/PlatformOrganizations";
import "./App.css";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Auth Route (redirect if logged in)
function AuthRoute({ children }) {
  const { user, loading, effectiveRole } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'super_admin') return <Navigate to="/platform" replace />;
    if (effectiveRole === 'guard') return <Navigate to="/dashboard" replace />;
    if (user.role === 'admin' || user.role === 'supervisor') return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Landing Route - shows landing for guests, redirects logged-in users
function LandingRoute({ children }) {
  const { user, loading, effectiveRole } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'super_admin') return <Navigate to="/platform" replace />;
    if (effectiveRole === 'guard') return <Navigate to="/dashboard" replace />;
    if (user.role === 'admin' || user.role === 'supervisor') return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Admin Route (only supervisors/admins)
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (!['supervisor', 'admin', 'super_admin'].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Super Admin Route (only super_admin)
function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Landing Page - Default homepage for visitors */}
          <Route path="/" element={<LandingRoute><Landing /></LandingRoute>} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
          <Route path="/register" element={<AuthRoute><Register /></AuthRoute>} />
          
          {/* Guard/Mobile Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
          <Route path="/patrol" element={<ProtectedRoute><Patrol /></ProtectedRoute>} />
          <Route path="/incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/sites" element={<AdminRoute><AdminSites /></AdminRoute>} />
          <Route path="/admin/schedules" element={<AdminRoute><AdminSchedules /></AdminRoute>} />
          <Route path="/admin/tracking" element={<AdminRoute><AdminTracking /></AdminRoute>} />
          <Route path="/admin/incidents" element={<AdminRoute><AdminIncidents /></AdminRoute>} />
          <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
          <Route path="/admin/organization" element={<AdminRoute><AdminOrganization /></AdminRoute>} />
          <Route path="/admin/join-requests" element={<AdminRoute><AdminJoinRequests /></AdminRoute>} />
          <Route path="/admin/timesheets" element={<AdminRoute><AdminTimesheets /></AdminRoute>} />
          <Route path="/admin/approvals" element={<AdminRoute><AdminApprovals /></AdminRoute>} />
          <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
          <Route path="/admin/activity" element={<AdminRoute><AdminActivityTimeline /></AdminRoute>} />
          
          {/* Platform Routes (Super Admin Only) */}
          <Route path="/platform" element={<SuperAdminRoute><PlatformDashboard /></SuperAdminRoute>} />
          <Route path="/platform/organizations" element={<SuperAdminRoute><PlatformOrganizations /></SuperAdminRoute>} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
